import { useEffect, useState } from "react";
import { 
    Container, Typography, Grid, Box, Paper, Avatar, Chip, 
    useTheme, Button, IconButton, alpha, Tooltip 
} from "@mui/material";

// Iconos
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTicket, faCalendarAlt, faStore, faQrcode } from "@fortawesome/free-solid-svg-icons";

// Componentes
import CustomBreadcrumbs from "../../../components/breadcrumbs";
import SectionTitle from "../../../components/sectionTitle";
import QUDProgress from "../../../components/progress";
import DonwloadableQR from "./downloadableQR"; // Tu modal mejorado

// API
import { GetAllCoupons } from "../../../controllers/customer/CouponsController";
import { imagesURL } from "../../../apiRoutes/apiBase";

// --- COMPONENTE TARJETA DE CUPÓN ---
const CouponCard = ({ coupon, onOpenQR }) => {
    const theme = useTheme();
    const isExpired = new Date(coupon.end_date) < new Date();
    const isActive = coupon.status === 'Emitido' && !isExpired;

    return (
        <Paper
            elevation={0}
            sx={{
                position: 'relative',
                borderRadius: 4,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                bgcolor: isActive ? '#fff' : '#f9f9f9',
                '&:hover': isActive ? {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.08)',
                    borderColor: theme.palette.primary.main
                } : {}
            }}
        >
            {/* Header con Imagen del Comercio */}
            <Box sx={{ 
                p: 2, 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2,
                borderBottom: '1px dashed #eee'
            }}>
                <Avatar 
                    src={imagesURL + 'empresas/' + coupon.merchant_imagen} 
                    sx={{ width: 50, height: 50, border: '1px solid #eee' }}
                >
                    <FontAwesomeIcon icon={faStore} />
                </Avatar>
                <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {coupon.merchant || 'Comercio'}
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                        {coupon.title}
                    </Typography>
                </Box>
            </Box>

            {/* Cuerpo del Cupón */}
            <Box sx={{ p: 2, flexGrow: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {coupon.description}
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip 
                        icon={<FontAwesomeIcon icon={faCalendarAlt} size="xs" />} 
                        label={`Expira: ${coupon.end_date}`} 
                        size="small" 
                        sx={{ bgcolor: 'grey.100', fontSize: '0.7rem' }}
                    />
                    {!isActive && (
                        <Chip label={isExpired ? "Vencido" : "Inactivo"} color="error" size="small" variant="outlined" />
                    )}
                </Box>
            </Box>

            {/* Footer con Acción */}
            <Box sx={{ p: 2, pt: 0 }}>
                <Button 
                    fullWidth 
                    variant="contained" 
                    color="primary"
                    disabled={isActive}
                    startIcon={<FontAwesomeIcon icon={faQrcode} />}
                    onClick={() => onOpenQR(coupon)}
                    sx={{ 
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 'bold',
                        boxShadow: 'none',
                        bgcolor: isActive ? theme.palette.primary.main : 'grey.300',
                        '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }
                    }}
                >
                    {isActive ? 'Ver Código QR' : 'No Disponible'}
                </Button>
            </Box>

            {/* Decoración de "Ticket" (Muescas laterales) */}
            <Box sx={{
                position: 'absolute', top: '78px', left: -8, width: 16, height: 16, borderRadius: '50%', bgcolor: '#F4F6F8' // Color del fondo de tu layout
            }} />
            <Box sx={{
                position: 'absolute', top: '78px', right: -8, width: 16, height: 16, borderRadius: '50%', bgcolor: '#F4F6F8' 
            }} />
        </Paper>
    );
};

export default function ClientCoupons() {
    const [coupons, setCoupons] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    
    // Estados para el Modal QR
    const [selectedCoupon, setSelectedCoupon] = useState(null);
    const [openQR, setOpenQR] = useState(false);

    useEffect(() => {
        handleGetCoupons();
    }, []);

    const handleGetCoupons = async () => {
        setIsLoading(true);
        let response = await GetAllCoupons();
        if (response.success === true) {
            setCoupons(response.data.coupons);
        }
        setIsLoading(false);
    }

    const handleOpenQR = (coupon) => {
        setSelectedCoupon(coupon);
        setOpenQR(true);
    }

    const handleCloseQR = () => {
        setOpenQR(false);
        // Opcional: Recargar cupones si al cerrar se marca como usado
        // handleGetCoupons(); 
    }

    return (
        <Container maxWidth="xl" sx={{ pb: 8 }}>
            <Box mb={4}>
                <CustomBreadcrumbs returnTo='/' crumbs={[{ name: 'Cupones', link: '/coupons' }]} />
                <Typography variant="h4" fontWeight="bold" sx={{ mt: 2 }}>
                    Mis Cupones
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Descuentos y promociones disponibles para canjear
                </Typography>
            </Box>

            {coupons.length > 0 ? (
                <Grid container spacing={3}>
                    {coupons.map((coupon, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <CouponCard coupon={coupon} onOpenQR={handleOpenQR} />
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <Paper sx={{ p: 5, textAlign: 'center', bgcolor: 'transparent', boxShadow: 'none' }}>
                     <Box sx={{ color: 'text.secondary', mb: 2 }}>
                        <FontAwesomeIcon icon={faTicket} size="4x" opacity={0.3} />
                     </Box>
                    <Typography variant="h6" color="text.secondary">
                        Aún no tienes cupones disponibles.
                    </Typography>
                </Paper>
            )}

            <QUDProgress open={isLoading} />
            
            {/* Modal QR Mejorado */}
            {selectedCoupon && (
                <DonwloadableQR 
                    open={openQR} 
                    coupon={selectedCoupon} 
                    handleClose={handleCloseQR} 
                />
            )}
        </Container>
    )
}